import type { MfaLoginFactorType } from '@auth0/auth0-acul-react/types';
import { useErrors } from '@auth0/auth0-acul-react/mfa-login-options';
import MfaChoice from 'creative-ui/oktaComponents/MfaChoice';
import CardBtn from 'creative-ui/uikit/CardBtn';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import emailSvg from '@/assets/e-mail.svg';
import maskGroupSvg from '@/assets/mask-group.svg';
import smartphoneMobileSvg from '@/assets/smartphone-mobile.svg';
import { AuthErrorBanner } from '@/components/AuthErrorBanner/AuthErrorBanner';
import { ContactAdvisorModal } from '@/components/ContactAdvisorModal/ContactAdvisorModal';
import Flex from '@/components/Layout/Flex/Flex';
import Text from '@/components/Typography/Text/Text';
import { useTrackErrors } from '@/hooks/useTrackErrors';

import { useMfaLoginOptionsManager } from '../hooks/useMfaLoginOptionsManager';

interface MfaLoginOptionsListProps {
  title: string;
  description: string;
}

function MfaLoginOptionsList({
  title,
  description,
}: Readonly<MfaLoginOptionsListProps>) {
  const { t } = useTranslation('mfaLoginOptions');
  const { handleSelectFactor, supportedEnrolledFactors, mfaLoginOptions } =
    useMfaLoginOptionsManager();

  const { errors } = useErrors();
  const { generalErrorMessages } = useTrackErrors(
    errors,
    'mfa/login/options/error',
  );

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // i18n strings
  const noOptionsText = t('list.noOptions');
  const genericError = t('errors.generic');

  const onSelectFactor = async (factor: MfaLoginFactorType) => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await handleSelectFactor(factor);
    } catch {
      setError(genericError);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (supportedEnrolledFactors.length === 0) {
    return (
      <>
        <h1 className="u-heading-2 u-textCenter u-margin-b-l">{title}</h1>
        <p className="u-textCenter">{noOptionsText}</p>
      </>
    );
  }

  const user = mfaLoginOptions.user;
  const enrolledPhoneNumber = user?.enrolledPhoneNumbers?.[0]?.phoneNumber;
  const enrolledEmail = user?.enrolledEmails?.[0]?.email;

  const cardConfig: Record<
    string,
    { img: string; title: string; text?: string }
  > = {
    sms: {
      img: smartphoneMobileSvg,
      title: t('cards.sms.title'),
      text: enrolledPhoneNumber ?? undefined,
    },
    email: {
      img: emailSvg,
      title: t('cards.email.title'),
      text: enrolledEmail ?? undefined,
    },
  };

  const cards = supportedEnrolledFactors.map((factor) => {
    const factorType = factor as MfaLoginFactorType;
    const config = cardConfig[factor];

    return (
      <CardBtn
        key={factor}
        title={
          config?.title ?? t(`factors.${factor}`, { defaultValue: factor })
        }
        img={config?.img}
        text={config?.text}
        textBreak
        onClick={() => onSelectFactor(factorType)}
      />
    );
  });

  return (
    <>
      <AuthErrorBanner errors={error ? [error] : generalErrorMessages} />
      <MfaChoice
        title={title}
        introText={description}
        cards={cards}
        modal={{
          label: t('modal.label'),
          modalContent: (
            <>
              <p className="u-heading-3 u-textCenter u-margin-b-l">
                {t('modal.title')}
              </p>
              <Flex justify="center" direction="row" align="center">
                <img alt="" src={maskGroupSvg} />
              </Flex>
              <div className="c-wysiwyg">
                <Text>{t('modal.description')}</Text>
                <Text>{t('modal.intro')}</Text>
                <ul>
                  <li className="u-list__item u-theme-color-before">
                    {t('modal.reasons.firstLogin')}
                  </li>
                  <li className="u-list__item u-theme-color-before">
                    {t('modal.reasons.newDevice')}
                  </li>
                  <li className="u-list__item u-theme-color-before">
                    {t('modal.reasons.privateBrowser')}
                  </li>
                  <li className="u-list__item u-theme-color-before">
                    {t('modal.reasons.newCountry')}
                  </li>
                </ul>
              </div>
            </>
          ),
        }}
      />

      <ContactAdvisorModal
        className="u-margin-t-l"
        ctaLabel={t('modalContact.ctaLabel')}
      />
    </>
  );
}

export default MfaLoginOptionsList;
